import React, { useRef } from 'react';
import BaseDataTable from 'react-data-table-component';

const DEFAULT_STICKY_BUTTON_WIDTH = 72;
const MAX_STICKY_BUTTON_WIDTH = 220;

const parsePixelValue = (value) => {
    if (typeof value === 'number') return value;
    if (typeof value !== 'string') return undefined;
    const match = value.match(/\d+(\.\d+)?/);
    return match ? Number(match[0]) : undefined;
};

const estimateWidthFromName = (name) => {
    if (typeof name !== 'string') return undefined;
    const estimated = name.length * 8 + 32;
    return Math.min(MAX_STICKY_BUTTON_WIDTH, Math.max(DEFAULT_STICKY_BUTTON_WIDTH, estimated));
};

const resolveButtonWidth = (column) => {
    const explicitWidth = Math.max(
        parsePixelValue(column?.width) || 0,
        parsePixelValue(column?.minWidth) || 0,
        parsePixelValue(column?.maxWidth) || 0
    );
    const estimated = estimateWidthFromName(column?.name) || DEFAULT_STICKY_BUTTON_WIDTH;

    return Math.max(explicitWidth, estimated, DEFAULT_STICKY_BUTTON_WIDTH);
};

const DataTable = ({ stickyActionPosition, className, columns, ...props }) => {
    const instanceClassRef = useRef(`lacivert-datatable-instance-${Math.random().toString(36).slice(2, 10)}`);
    const instanceClassName = instanceClassRef.current;
    const safeColumns = Array.isArray(columns) ? columns : [];
    const normalizedColumns = safeColumns.map((column, index) => {
        if (!column) return column;
        const normalizedId = column?.id ?? `col-${index}`;

        return {
            ...column,
            id: normalizedId,
        };
    });

    const initialButtonIndexes = normalizedColumns
        .map((column, index) => (column?.button ? index : -1))
        .filter(index => index !== -1);

    const hasButtonColumn = initialButtonIndexes.length > 0;
    const resolvedPosition = stickyActionPosition || (hasButtonColumn ? 'right' : undefined);
    const orderedColumns = resolvedPosition === 'left' && hasButtonColumn
        ? [
            ...normalizedColumns.filter(column => column?.button),
            ...normalizedColumns.filter(column => !column?.button),
        ]
        : normalizedColumns;

    const buttonColumnIndexes = orderedColumns
        .map((column, index) => (column?.button ? index : -1))
        .filter(index => index !== -1);
    const stickyClass = resolvedPosition === 'right'
        ? 'lacivert-datatable--sticky-right'
        : resolvedPosition === 'left'
            ? 'lacivert-datatable--sticky-left'
            : '';

    const stickyOffsets = new Map();
    if (resolvedPosition && hasButtonColumn) {
        if (resolvedPosition === 'right') {
            let offset = 0;
            [...buttonColumnIndexes].reverse().forEach((index) => {
                stickyOffsets.set(index, offset);
                offset += resolveButtonWidth(orderedColumns[index]);
            });
        } else {
            let offset = 0;
            buttonColumnIndexes.forEach((index) => {
                stickyOffsets.set(index, offset);
                offset += resolveButtonWidth(orderedColumns[index]);
            });
        }
    }

    const stickyStyles = resolvedPosition && hasButtonColumn
        ? buttonColumnIndexes
            .map((index) => {
                const columnId = String(orderedColumns[index]?.id ?? `col-${index}`);
                const offsetValue = stickyOffsets.get(index) || 0;
                const side = resolvedPosition;
                const sidePadding = side === 'right' ? 'padding-left: 12px;' : 'padding-right: 12px;';
                return `
                    .${instanceClassName} .rdt_TableCol[data-column-id="${columnId}"],
                    .${instanceClassName} .rdt_TableCell[data-column-id="${columnId}"] {
                        position: sticky;
                        ${side}: ${offsetValue}px;
                        background: #ffffff;
                        box-sizing: border-box;
                        ${sidePadding}
                    }

                    .${instanceClassName} .rdt_TableCol[data-column-id="${columnId}"] {
                        z-index: 4;
                    }

                    .${instanceClassName} .rdt_TableCell[data-column-id="${columnId}"] {
                        z-index: 2;
                    }
                `;
            })
            .join('\n')
        : '';

    const finalColumns = orderedColumns.map((column, index) => {
        if (!column) return column;

        const buttonColumn = Boolean(column?.button);
        const isStickyButton = stickyOffsets.has(index);
        const lockedWidth = isStickyButton ? resolveButtonWidth(column) : undefined;
        const buttonColumnClass = buttonColumn ? 'lacivert-datatable__button-col' : '';

        const centeredStyle = buttonColumn
            ? {
                textAlign: 'center',
                justifyContent: 'center',
                paddingLeft: '12px',
                paddingRight: '12px',
            }
            : {};

        return {
            ...column,
            ...(isStickyButton
                ? {
                    width: column?.width ?? `${lockedWidth}px`,
                    minWidth: column?.minWidth ?? `${lockedWidth}px`,
                    maxWidth: column?.maxWidth ?? `${lockedWidth}px`,
                }
                : {}),
            center: buttonColumn ? true : column?.center,
            right: buttonColumn ? false : column?.right,
            className: [column?.className, buttonColumnClass].filter(Boolean).join(' '),
            headCellClassName: [column?.headCellClassName, buttonColumnClass].filter(Boolean).join(' '),
            style: {
                ...(column?.style || {}),
                ...centeredStyle,
            },
            headCellStyle: {
                ...(column?.headCellStyle || {}),
                ...centeredStyle,
            },
        };
    });

    const mergedClassName = ['lacivert-datatable', instanceClassName, className, stickyClass]
        .filter(Boolean)
        .join(' ');

    return (
        <>
            {stickyStyles && <style>{stickyStyles}</style>}
            <BaseDataTable
                {...props}
                columns={finalColumns}
                className={mergedClassName}
            />
        </>
    );
};

export default DataTable;
